import React from "react";
import { Model, HighsRawSolution } from "../../powerFlow/highs";
import { extractObjectiveTables } from "../../powerFlow/objectiveTerms";
import { inputHeader, inputCell, outputHeader, outputCell } from "./tableStyles";

interface Props {
  model?: Model;
  solution?: HighsRawSolution;
  solutionValid?: boolean;
  solutionMessage?: string;
  /**
   * The whole objective (`minimize cost = …`), rendered. Read-only: the sense
   * and the name are fixed, and what it sums is the enabled cost categories,
   * each of which is edited in its own panel.
   */
  objectiveText?: string;
}

// The composed objective, shown the way a rule is shown above its rows.
const objectiveStyle: React.CSSProperties = {
  padding: "6px 8px",
  fontFamily: "monospace",
  fontSize: "12px",
  color: "#555",
  background: "#fbfbf6",
  border: "1px solid #eee",
  borderRadius: "3px",
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
};

/**
 * Overview for the "costs" (objective) folder: the objective it all adds up to,
 * the solver status, the overall cost, and a per-category breakdown. Each
 * category's own terms live in its leaf alongside this one, where it's editable.
 */
const ObjectiveOverview: React.FC<Props> = ({
  model,
  solution,
  solutionValid,
  solutionMessage,
  objectiveText,
}) => {
  const objectiveBlock = objectiveText && <div style={objectiveStyle}>{objectiveText}</div>;

  if (!model) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
        {objectiveBlock}
        <div style={{ color: "#666", fontStyle: "italic" }}>
          No solver information available. Run a power flow calculation to see the
          objective.
        </div>
      </div>
    );
  }

  const tables = extractObjectiveTables(model, solution);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
      {objectiveBlock}

      {/* Solver status */}
      <div
        style={{
          padding: "8px",
          background: solutionValid ? "#e8f5e8" : "#ffeaea",
          border: `1px solid ${solutionValid ? "#4caf50" : "#f44336"}`,
          borderRadius: "4px",
        }}
      >
        <div style={{ fontWeight: "bold" }}>
          Solution Status: {solutionValid ? "✓ Valid" : "✗ Invalid"}
        </div>
        {solutionMessage && (
          <div style={{ fontSize: "12px", color: "#666", marginTop: "4px" }}>
            {solutionMessage}
          </div>
        )}
      </div>

      {/* Overall cost */}
      <div>
        <strong>{model.objective.sense} — total cost:</strong>{" "}
        {solution?.ObjectiveValue !== undefined
          ? `$${solution.ObjectiveValue.toFixed(2)}`
          : "N/A"}
      </div>

      {/* Per-class cost breakdown */}
      {tables.length > 0 && (
        <div>
          <div style={{ fontWeight: "bold", marginBottom: "4px" }}>By category</div>
          <table style={{ borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={inputHeader}>category</th>
                <th style={{ ...outputHeader, textAlign: "right" }}>cost</th>
              </tr>
            </thead>
            <tbody>
              {tables.map((table) => {
                const subtotal = table.rows.reduce(
                  (sum, row) => sum + (row.cost ?? 0),
                  0
                );
                return (
                  <tr key={table.name}>
                    <td style={inputCell}>{table.name}</td>
                    <td style={{ ...outputCell, textAlign: "right" }}>
                      ${subtotal.toFixed(2)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ObjectiveOverview;
